/**
 * Менеджеры.
 *
 * Вопросы: кто приносит выручку и прибыль; не держится ли продажа на одном
 * человеке; кто продаёт много, но дёшево; кто растёт, а кто падает за год.
 *
 * Источник — отчёт «ABC-анализ клиентов»: менеджер есть только в нём.
 * Маржа — от выручки строк с себестоимостью, как во всей системе.
 * Продажи без менеджера показаны отдельной строкой, а не спрятаны:
 * это доля выручки, у которой нет ответственного.
 */

import { useState, type CSSProperties } from 'react'
import { useFinance } from '../../finance/context'
import { indexBy, num, rowKey, useReport, useSettled, type ReportState } from '../../finance/data'
import { BarRows, ColumnChart } from '../../finance/charts'
import { Caveat, DataNotes, Delta, Kpi, Panel, Segmented, SkeletonColumns, SkeletonRows } from '../../finance/parts'
import { originFrom, type DrawerOrigin } from '../../finance/Drawer'
import { usePublishFreshness } from '../../finance/freshness'
import { compareLabel, labelOf, monthLabel, monthsOf, shortMonth } from '../../finance/period'
import { missingOf, recentWindows } from '../../finance/coverage'
import { salesEmpty } from '../../finance/frame'
import { SALES_SERIES, money } from '../../finance/sections/Overview'
import { deltaPercent, formatMoneyShort, formatPercent, pluralize, toNumber } from '../../format'
import { MiniColumns } from '../charts'
import { hasMetric, labelOr, ranked } from '../parts'

const NO_MANAGER = 'Менеджер не указан'

function useMarginAllowed(): boolean {
  const { catalog } = useFinance()
  const metrics = catalog.descriptor('abc_customers')?.metrics ?? []
  return metrics.some((item) => item.key === 'gross_profit') && metrics.some((item) => item.key === 'margin_percent')
}

function useManagers(range: { from: string; to: string }, groupBy: string[], metrics: string[], filters?: Record<string, string[]>): ReportState {
  const { scoped } = useFinance()
  return useReport(
    'abc_customers',
    scoped('abc_customers', { period: range, group_by: groupBy, metrics, filters, order_by: { key: 'revenue', direction: 'desc' } }),
  )
}

export function Managers({ onOpenManager }: { onOpenManager: (target: { value: string; label: string }, origin: DrawerOrigin) => void }) {
  const live = useFinance()
  const withMargin = useMarginAllowed()
  const metrics = withMargin ? ['revenue', 'gross_profit', 'margin_percent'] : ['revenue']

  const currentLive = useManagers(live.range, ['manager'], metrics)
  const beforeLive = useManagers(live.previousRange, ['manager'], metrics)
  const pairsLive = useManagers(live.range, ['manager', 'counterparty'], ['revenue'])
  const monthlyLive = useManagers(live.trend, ['period', 'manager'], ['revenue'])
  usePublishFreshness([currentLive, monthlyLive])

  // Период, прошлый период и динамика — одним набором и с подписями того
  // выбора, к которому они относятся (ctx ниже — выбор показанных цифр).
  const settled = useSettled([currentLive, beforeLive, pairsLive, monthlyLive], live)
  const [current, before, pairs, monthly] = settled.states
  const ctx = settled.view
  const { range, trend, selection, scopeLabel, coverage, noCompare, gap } = ctx
  const incomplete = Boolean(noCompare)
  const vs = compareLabel(selection)

  const [order, setOrder] = useState<'revenue' | 'margin'>('revenue')

  const rows = ranked(current.data?.rows)
  const named = rows.filter((row) => row.manager_guid)
  const unassigned = rows.filter((row) => !row.manager_guid).reduce((sum, row) => sum + num(row.revenue), 0)
  const total = num(current.data?.totals.revenue)
  const previous = indexBy(before.data?.rows, 'manager')
  const marginShown = withMargin && hasMetric(current.data?.columns, 'margin_percent')
  const avgMargin = toNumber(current.data?.totals.margin_percent)

  const clientsOf = new Map<string, number>()
  for (const row of pairs.data?.rows ?? []) {
    if (num(row.revenue) <= 0) continue
    const key = rowKey(row, 'manager')
    clientsOf.set(key, (clientsOf.get(key) ?? 0) + 1)
  }

  const top3 = named.slice(0, 3).reduce((sum, row) => sum + num(row.revenue), 0)
  const byMargin = named.filter((row) => toNumber(row.margin_percent) !== null).sort((a, b) => num(a.margin_percent) - num(b.margin_percent))
  const lowest = byMargin[0]
  const highest = byMargin[byMargin.length - 1]
  const list = marginShown && order === 'margin' ? [...rows].sort((a, b) => num(b.margin_percent) - num(a.margin_percent)) : rows

  // --- Динамика -------------------------------------------------------------
  const months = monthsOf(trend)
  const rangeMonths = new Set(monthsOf(range))
  const series = new Map<string, { label: string; guid: string | null; values: Map<string, number>; total: number }>()
  for (const row of monthly.data?.rows ?? []) {
    const key = rowKey(row, 'manager') || '∅'
    const entry = series.get(key) ?? { label: labelOr(row.manager, NO_MANAGER), guid: row.manager_guid ? String(row.manager_guid) : null, values: new Map(), total: 0 }
    const month = String(row.period).slice(0, 10)
    entry.values.set(month, (entry.values.get(month) ?? 0) + num(row.revenue))
    entry.total += num(row.revenue)
    series.set(key, entry)
  }
  const trendRows = [...series.values()].filter((item) => item.total > 0).sort((a, b) => b.total - a.total)
  const trendMax = Math.max(1, ...trendRows.flatMap((item) => [...item.values.values()]))
  // «3 к 3» — только по загруженным месяцам: хвост без выгрузки не падение.
  const windows = recentWindows(months, coverage)
  const halfChange = (values: Map<string, number>) => {
    if (!windows) return null
    const sum = (list: string[]) => list.reduce((acc, month) => acc + (values.get(month) ?? 0), 0)
    return deltaPercent(sum(windows.recent), sum(windows.prior))
  }
  const noDocuments = current.status === 'ready' && rows.length === 0

  const state = current.status
  return (
    <div className="fin-stack insights">
      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}
      <div className="kpi-grid">
        <Kpi
          index={0}
          state={state}
          label="Менеджеров с продажами"
          value={gap ? null : named.length}
          format={(value) => String(Math.round(value))}
          memoryKey="mgr-count"
          hint={gap ? 'нет данных за период' : named.length > 0 ? `в среднем ${money(named.reduce((sum, row) => sum + num(row.revenue), 0) / named.length)} на менеджера` : scopeLabel}
        />
        <Kpi
          index={1}
          state={state}
          label="Доля трёх крупнейших"
          value={total > 0 && named.length > 3 ? (top3 / total) * 100 : null}
          format={(value) => formatPercent(value)}
          memoryKey="mgr-top3"
          hint={
            noDocuments
              ? gap ? 'нет данных за период' : 'В периоде нет продаж'
              : named.length > 3
              ? `${named.slice(0, 3).map((row) => String(row.manager)).join(', ')} — ${money(top3)}`
              : 'Менеджеров три или меньше — доля лидеров ничего не говорит'
          }
        />
        <Kpi
          index={2}
          state={state}
          label="Продажи без менеджера"
          value={noDocuments ? null : unassigned}
          format={money}
          memoryKey="mgr-none"
          tone={total > 0 && unassigned / total > 0.05 ? 'bad' : undefined}
          hint={
            noDocuments
              ? gap ? 'нет данных за период' : 'В периоде нет продаж — проверять нечего'
              : unassigned > 0
                ? `${formatPercent((unassigned / total) * 100)} выручки — в документах 1С менеджер не указан`
                : 'Во всех документах периода менеджер указан'
          }
        />
        {withMargin ? (
          <Kpi
            index={3}
            state={state}
            label="Самая низкая маржа"
            value={lowest ? toNumber(lowest.margin_percent) : null}
            format={(value) => formatPercent(value)}
            memoryKey="mgr-low"
            hint={
              noDocuments
                ? gap ? 'нет данных за период' : 'В периоде нет продаж'
                : lowest && highest && lowest !== highest
                  ? `${String(lowest.manager)}; самая высокая — ${formatPercent(highest.margin_percent)} у ${String(highest.manager)}`
                  : 'Сравнивать не с кем'
            }
          />
        ) : (
          <Kpi index={3} state="forbidden" label="Маржа по менеджерам" value={null} format={String} memoryKey="mgr-low" closedText="Валовая прибыль не входит в ваши права" />
        )}
      </div>

      <Panel
        index={1}
        title={marginShown ? 'Выручка и валовая прибыль по менеджерам' : 'Выручка по менеджерам'}
        subtitle={`Без НДС · ${scopeLabel}${incomplete ? '' : ` · справа — выручка ${vs}`}`}
        states={[current]}
        skeleton={<SkeletonRows rows={6} />}
        empty={salesEmpty(ctx, rows.length === 0)}
        actions={
          marginShown ? (
            <Segmented
              size="sm"
              label="Порядок"
              value={order}
              onChange={setOrder}
              options={[
                { value: 'revenue', label: 'По выручке' },
                { value: 'margin', label: 'По марже' },
              ]}
            />
          ) : undefined
        }
      >
        {() => (
          <>
            {marginShown && <BarLegend />}
            <BarRows
              ariaLabel="Выручка по менеджерам"
              series={marginShown ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
              rows={list.map((row) => {
                const key = rowKey(row, 'manager') || '∅'
                const clients = clientsOf.get(rowKey(row, 'manager')) ?? 0
                const share = total > 0 ? formatPercent((num(row.revenue) / total) * 100) : '—'
                return {
                  key,
                  label: labelOr(row.manager, NO_MANAGER),
                  sub: [
                    marginShown ? `маржа ${formatPercent(row.margin_percent)}` : null,
                    share,
                    pairs.status === 'ready' ? pluralize(clients, 'клиент', 'клиента', 'клиентов') : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                  values: marginShown ? [num(row.revenue), num(row.gross_profit)] : [num(row.revenue)],
                  trend: monthly.status === 'ready' ? months.map((month) => (missingOf(month, coverage) ? null : (series.get(key)?.values.get(month) ?? 0))) : undefined,
                  aside: incomplete ? null : <Delta value={deltaPercent(row.revenue, previous.get(key === '∅' ? '' : key)?.revenue)} />,
                }
              })}
              onSelect={(row, element) => {
                if (row.key === '∅') return
                onOpenManager({ value: row.key, label: row.label }, originFrom(element))
              }}
            />
            {marginShown && avgMargin !== null && (
              <div className="tiny muted" style={{ marginTop: 10 }}>
                Средняя маржа по выдаче — {formatPercent(avgMargin)}. Маржа — от выручки строк с себестоимостью.
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel
        index={2}
        title="Динамика за 12 месяцев"
        subtitle={`Выручка без НДС по месяцам, у всех строк одна шкала · справа — ${
          windows ? `${windows.label} (закончившиеся месяцы с данными)` : 'сравнение 3 к 3 не считается: нет шести закончившихся месяцев с данными подряд'
        }`}
        states={[monthly]}
        skeleton={<SkeletonRows rows={5} height={28} />}
        empty={salesEmpty(ctx, trendRows.length === 0, 'Продаж за 12 месяцев нет')}
      >
        {() => (
          <div className="trend-rows">
            {trendRows.map((item, index) => (
              <div key={item.guid ?? '∅'} className="trend-rows__row cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                <span className="bars__label">
                  <span className="bars__name">{item.label}</span>
                  <span className="bars__sub">за 12 месяцев {money(item.total)}</span>
                </span>
                <MiniColumns
                  ariaLabel={`${item.label}: выручка по месяцам`}
                  values={months.map((month) => item.values.get(month) ?? null)}
                  missing={months.map((month) => missingOf(month, coverage))}
                  labels={months.map(monthLabel)}
                  max={trendMax}
                />
                <span className="trend-rows__aside">
                  <Delta value={halfChange(item.values)} label={windows ? undefined : 'нет данных'} />
                </span>
              </div>
            ))}
            <div className="trend-rows__axis" aria-hidden="true">
              <span />
              <span className="mini-columns mini-columns--labels">
                {months.map((month) => (
                  <span
                    key={month}
                    className={`mini-columns__label${rangeMonths.has(month) ? ' mini-columns__label--on' : ''}${missingOf(month, coverage) ? ' mini-columns__label--missing' : ''}`}
                  >
                    {shortMonth(month)}
                  </span>
                ))}
              </span>
              <span />
            </div>
          </div>
        )}
      </Panel>

      {unassigned > 0 && total > 0 && unassigned / total > 0.05 && (
        <Caveat tone="warn">
          {formatPercent((unassigned / total) * 100)} выручки — {money(unassigned)} — без менеджера. Эти продажи не попадут ни в один
          показатель по людям; поле «Менеджер» заполняется в документе реализации 1С.
        </Caveat>
      )}

      <DataNotes states={[current, monthly]} />
    </div>
  )
}

function BarLegend() {
  return (
    <div className="chart-legend" style={{ padding: '0 0 12px' }}>
      {SALES_SERIES.map((item) => (
        <span className="chart-legend__item" key={item.key}>
          <span className="chart-legend__swatch" style={{ background: item.color }} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  )
}

// --- Детализация менеджера --------------------------------------------------

export function ManagerDrill({ value }: { value: string }) {
  const { range, trend, selection, coverage } = useFinance()
  const withMargin = useMarginAllowed()
  const metrics = withMargin ? ['revenue', 'gross_profit', 'margin_percent'] : ['revenue']
  const filters = { manager: [value] }
  const totals = useManagers(range, ['manager'], metrics, filters)
  const monthly = useManagers(trend, ['period'], metrics, filters)
  const clients = useManagers(range, ['counterparty'], metrics, filters)

  const months = monthsOf(trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(range))
  const sum = totals.data?.totals ?? {}
  const clientRows = ranked(clients.data?.rows)
  const clientTotal = clientRows.reduce((acc, row) => acc + num(row.revenue), 0)
  const topShare = clientTotal > 0 && clientRows[0] ? (num(clientRows[0].revenue) / clientTotal) * 100 : null

  return (
    <div className="drill-step stack" style={{ gap: 18 }}>
      <div className="drill-kpis">
        {[
          { label: 'Выручка без НДС', text: money(num(sum.revenue)) },
          ...(withMargin
            ? [
                { label: 'Валовая прибыль', text: money(num(sum.gross_profit)) },
                { label: 'Маржа', text: formatPercent(sum.margin_percent) },
              ]
            : []),
          { label: 'Клиентов', text: clients.status === 'ready' ? String(clientRows.length) : '…' },
        ].map((item, index) => (
          <div key={item.label} className="drill-kpi cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
            <div className="tiny muted">{item.label}</div>
            <div className="drill-kpi__value mono">{totals.status === 'ready' ? item.text : '…'}</div>
          </div>
        ))}
      </div>

      <Panel title="Выручка по месяцам" subtitle="12 месяцев, выбранный период подсвечен" states={[monthly]} skeleton={<SkeletonColumns height={150} />}>
        {() => (
          <ColumnChart
            ariaLabel="Выручка менеджера по месяцам"
            height={150}
            series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
            summaryTitle={labelOf(selection)}
            points={months.map((month) => ({
              key: month,
              label: shortMonth(month),
              title: monthLabel(month),
              highlight: rangeMonths.has(month),
              missing: missingOf(month, coverage),
              values: withMargin ? [num(byMonth.get(month)?.revenue), num(byMonth.get(month)?.gross_profit)] : [num(byMonth.get(month)?.revenue)],
            }))}
          />
        )}
      </Panel>

      <Panel
        title="Клиенты менеджера"
        subtitle={topShare !== null ? `Крупнейший клиент — ${formatPercent(topShare)} его выручки` : 'За выбранный период'}
        states={[clients]}
        skeleton={<SkeletonRows rows={5} />}
        empty={{ when: clientRows.length === 0, title: 'Продаж за период нет' }}
      >
        {() => (
          <>
            <BarRows
              ariaLabel="Выручка менеджера по клиентам"
              series={SALES_SERIES.slice(0, 1)}
              rows={clientRows.slice(0, 12).map((row) => ({
                key: rowKey(row, 'counterparty'),
                label: labelOr(row.counterparty, 'Клиент не указан'),
                sub: withMargin ? `маржа ${formatPercent(row.margin_percent)}` : undefined,
                values: [num(row.revenue)],
              }))}
            />
            {clientRows.length > 12 && (
              <div className="tiny muted" style={{ marginTop: 8 }}>
                Показаны 12 крупнейших из {clientRows.length}; остальные — {formatMoneyShort(clientRows.slice(12).reduce((acc, row) => acc + num(row.revenue), 0))} ₽.
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  )
}
